import { useClients } from 'core/app/AppContext';
import { useState, createContext, useContext, PropsWithChildren } from 'react';
import { ClientLevel } from '../../../core/types';
import { ConsentFileData, ConsentFileType } from '../types';

export const ImportConsentContext = createContext(null);

export interface ImportConsentState {
  analysedFileData: ConsentFileData;
  setAnalysedFileData: (data: ConsentFileData) => void;
  analysedFileModalVisibility: boolean;
  setAnalysedFileModalVisibility: (show: boolean) => void;
  clientId: string;
  setClientId: (clientId: string) => void;
  clientLevel: ClientLevel;
  setClientLevel: (level: ClientLevel) => void;
  dateHeadingSelected: string;
  setDateHeadingSelected: (heading: string) => void;
  emailHeadingSelected: string;
  setEmailHeadingSelected: (heading: string) => void;
  fileData: ConsentFileData[];
  setFileData: (files: ConsentFileData[]) => void;
  filesToProcess: { [consentType: string]: number };
  setFilesToProcess: (filesToProcess: {}) => void;
  fileType: ConsentFileType;
  setFileType: (fileType: ConsentFileType) => void;
  importFileModalVisibility: boolean;
  setImportFileModalVisibility: (show: boolean) => void;
  successUploadCount: number;
  setSuccessUploadCount: (count: number) => void;
  uploadFiles: ConsentFileData[];
  setUploadFiles: (uploadFiles: ConsentFileData[]) => void;
}

export type ImportConsentProviderProps = PropsWithChildren<{
  analysedFileData?: ConsentFileData;
  analysedFileModalVisibility?: boolean;
  clientId?: string;
  clientLevel?: ClientLevel;
  dateHeadingSelected?: string;
  emailHeadingSelected?: string;
  fileData?: ConsentFileData[];
  filesToProcess?: { [consentType: string]: number };
  fileType?: ConsentFileType;
  importFileModalVisibility?: boolean;
  successUploadCount?: number;
  uploadFiles?: ConsentFileData[];
}>;

export function ImportConsentProvider(props: ImportConsentProviderProps) {
  const [analysedFileData, setAnalysedFileData] = useState(props.analysedFileData ?? null);
  const [analysedFileModalVisibility, setAnalysedFileModalVisibility] = useState(
    props.analysedFileModalVisibility ?? false,
  );
  const [clientId, setClientId] = useState(props.clientId ?? null);
  const [clientLevel, setClientLevel] = useState<ClientLevel>(props.clientLevel ?? ClientLevel.SITE);
  const [dateHeadingSelected, setDateHeadingSelected] = useState(props.dateHeadingSelected ?? null);
  const [emailHeadingSelected, setEmailHeadingSelected] = useState(props.emailHeadingSelected ?? null);
  const [fileData, setFileData] = useState(props.fileData ?? []);
  const [filesToProcess, setFilesToProcess] = useState(props.filesToProcess ?? {});
  const [fileType, setFileType] = useState<ConsentFileType | null>(props.fileType ?? null);
  const [importFileModalVisibility, setImportFileModalVisibility] = useState(props.importFileModalVisibility ?? false);
  const [successUploadCount, setSuccessUploadCount] = useState(props.successUploadCount ?? 0);
  const [uploadFiles, setUploadFiles] = useState(props.uploadFiles ?? []);

  const state: ImportConsentState = {
    analysedFileData,
    setAnalysedFileData,
    analysedFileModalVisibility,
    setAnalysedFileModalVisibility,
    clientId,
    setClientId,
    clientLevel,
    setClientLevel,
    dateHeadingSelected,
    setDateHeadingSelected,
    emailHeadingSelected,
    setEmailHeadingSelected,
    fileData,
    setFileData,
    filesToProcess,
    setFilesToProcess,
    fileType,
    setFileType,
    importFileModalVisibility,
    setImportFileModalVisibility,
    successUploadCount,
    setSuccessUploadCount,
    uploadFiles,
    setUploadFiles,
  };

  return <ImportConsentContext.Provider value={state} {...props} />;
}

export function useImportConsentContext() {
  const context: ImportConsentState = useContext(ImportConsentContext);
  if (!context) {
    throw new Error(`useImportConsentContext must be used within a ImportConsentProvider`);
  }

  return { ...context };
}

export function useConsentClientName(): null | string {
  const context: ImportConsentState = useContext(ImportConsentContext);
  const { getBrand, getSite } = useClients();
  const { clientId, clientLevel } = context;

  if (!clientId || !clientLevel) {
    return null;
  }

  const fetchedClientName = clientLevel === ClientLevel.SITE ? getSite(clientId)?.name : getBrand(clientId)?.name;

  return fetchedClientName || 'unknown client';
}
